import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { userNavigationHistoryStore } from '../../app/stores/userNavigationHistoryStore.js';
import { Button } from '@fabric-app/ui/components/Button';
import { useContextShareToken, useContextShareTokenExtraPermissions } from '../../permission/contexts/AnonShareTokenContext.js';
import { useMutationJoinViaShareToken } from '../../permission/mutations/useMutationJoinViaShareToken.js';
import { useQueryResourceMemberPermissions } from '../../permission/queries/useQueryResourceMemberPermissions.js';
import useInviteStore from '../../store/invite.js';
import { useRouter, useAuthStatus } from '@fabric-app/compat';
import { getResourcePath } from '../../permission/utils/useRedirectFromShareToken.js';
import { IconPlusSmall } from '@fabric-app/icons/svg';
import { NoSsr } from '@fabric-app/ui/components/NoSSR';
import { useMutationCopyResources } from '../../resources/mutations/useMutationCopyResource.js';
import { useQueryInbox } from '../../connections/queries/useQueryInbox.js';
import { toast } from '../../store/alerts.js';
import { usePostSigninRedirect } from '../../auth/hooks/usePostSigninRedirect.js';
import { motion } from 'framer-motion';
import { useMutationCreateNewSpace } from '../mutations/useMutationCreateNewSpace.js';

const AddToLibraryButton = ({ type, resource }) => {
  const shareToken = useContextShareToken();
  const router = useRouter();
  const resetNavigationHistory = userNavigationHistoryStore((s) => s.resetHistory);
  const { isCurrentUserMember } = useQueryResourceMemberPermissions({
    resourceId: shareToken?.resourceId
  });
  if (!shareToken) {
    return null;
  }
  if (isCurrentUserMember) {
    return /* @__PURE__ */ jsx(Button, {
      style: {
        width: "auto"
      },
      variant: "primary-inverse",
      onClick: async () => {
        const redirectTo = getResourcePath({
          resourceId: shareToken.resourceId,
          type
        });
        if (redirectTo) {
          await router.push(redirectTo);
          resetNavigationHistory();
        }
      },
      children: "Open in dashboard –>"
    });
  }
  if (shareToken?.type === "publishing") {
    if (!resource) {
      return null;
    }
    return /* @__PURE__ */ jsx(CopyToFabricButton, {
      resource,
      accessToken: shareToken.accessToken
    });
  }
  return /* @__PURE__ */ jsx(JoinButton, {
    type
  });
};
const JoinButton = ({ type }) => {
  const shareToken = useContextShareToken();
  const mutationJoin = useMutationJoinViaShareToken();
  const authStatus = useAuthStatus();
  const router = useRouter();
  const storeInviteBeforeLogin = useInviteStore((s) => s.setInvite);
  if (!shareToken || !authStatus) {
    return null;
  }
  return /* @__PURE__ */ jsx(NoSsr, {
    children: /* @__PURE__ */ jsx(Button, {
      style: {
        width: "auto"
      },
      variant: "primary-inverse",
      isLoading: mutationJoin.isPending,
      onClick: async () => {
        if (authStatus === "unauthenticated") {
          storeInviteBeforeLogin({
            ...shareToken,
            type: "space"
          });
          const randomString = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
          return router.push("/beta-sign-up-exclusive-link/" + randomString);
        }
        mutationJoin.mutate({
          ...shareToken,
          action: "invite-page-add-to-library",
          type
        });
      },
      "data-testid": "space-view-join-button",
      children: authStatus === "unauthenticated" ? "Log in to collaborate" : /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(IconPlusSmall, {
            height: 10,
            width: 10
          }),
          "Add to your library"
        ]
      })
    })
  });
};
const CopyToFabricButton = ({ resource, accessToken }) => {
  const { mutate: mutateCopyResource, isPending: isPendingCopyResource } = useMutationCopyResources();
  const { mutate: createSpace, isPending: isPendingCreateSpace } = useMutationCreateNewSpace();
  const authStatus = useAuthStatus();
  const { inboxRoot } = useQueryInbox();
  const { disabledCopy } = useContextShareTokenExtraPermissions();
  const { redirectToSignin } = usePostSigninRedirect();
  if (disabledCopy) {
    return null;
  }
  return /* @__PURE__ */ jsx(NoSsr, {
    children: /* @__PURE__ */ jsx(Button, {
      style: {
        width: "auto"
      },
      variant: "primary-inverse",
      isLoading: isPendingCopyResource || isPendingCreateSpace,
      as: motion.button,
      layout: true,
      layoutId: "copy-to-fabric-button",
      onClick: () => {
        if (authStatus === "unauthenticated") {
          redirectToSignin();
          return;
        }
        if (resource.kind === "folder") {
          createSpace({
            copyFolderId: resource.id,
            action: "copy-published-space"
          }, {
            onSuccess: () => {
              toast({
                id: "copy-resource-success",
                content: "Copying over to your Fabric..."
              });
            }
          });
          return;
        }
        if (!inboxRoot) {
          toast({
            id: "copy-resource-error",
            content: "Inbox not found, please try again in a moment."
          });
          return;
        }
        mutateCopyResource({
          resources: [
            resource
          ],
          parent: {
            id: inboxRoot.id,
            name: "Inbox"
          },
          disableToast: true,
          accessToken
        }, {
          onSuccess: () => {
            toast({
              id: "resource-paste",
              content: "Copied to your Inbox"
            });
          }
        });
      },
      "data-testid": "space-view-join-button",
      children: "Copy to your Fabric"
    })
  });
};

export { AddToLibraryButton };
//# sourceMappingURL=AddToLibraryButton.js.map
